import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { Settings2, Trash2 } from "lucide-react";
import TileSelector, { TileOption } from "../TileSelector.tsx";
import { useSupportedLanguages } from "../../api/languageApi.ts";
import {
  getTranslation,
  useInstrumentsWithFilters,
} from "../../api/instrumentApi.ts";
import { useLanguageStore } from "../../stores/languageStore.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { useSupportedGenres } from "../../api/genreApi.ts";
import { SearchModal } from "./SearchModal.tsx";

interface SearchBarProps {
  className?: string;
}

const Filter: React.FC<SearchBarProps> = ({ className = "" }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { data: supportedGenres } = useSupportedGenres();
  const { data: instrumentsWithFilters } = useInstrumentsWithFilters();
  const { data: supportedLanguages } = useSupportedLanguages();
  const { language } = useLanguageStore();
  const { t } = useLingui();

  // Search state
  const instrument = searchParams.get("instrument") ?? "";
  const [searchInstrumentFilters, setSearchInstrumentFilters] = useState<
    string[]
  >([]);
  const [searchGenres, setSearchGenres] = useState<string[]>([]);
  const [searchLanguages, setSearchLanguages] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    setSearchInstrumentFilters(searchParams.getAll("filters"));
    setSearchGenres(searchParams.getAll("genres"));
    setSearchLanguages(searchParams.getAll("languages"));
  }, [searchParams]);

  const handleSearch = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete("filters");
    searchInstrumentFilters.forEach((f) => params.append("filters", f));
    params.delete("genres");
    searchGenres.forEach((g) => params.append("genres", g));
    params.delete("languages");
    searchLanguages.forEach((l) => params.append("languages", l));
    navigate(`/search?${params.toString()}`);
    setIsOpen(false);
  }, [
    navigate,
    searchGenres,
    searchInstrumentFilters,
    searchLanguages,
    searchParams,
  ]);

  const instrumentWithFilters = useMemo(() => {
    return instrumentsWithFilters?.find((v) => v.name === instrument);
  }, [instrument, instrumentsWithFilters]);

  const instrumentFilterOptions: TileOption[] = useMemo(
    () =>
      instrumentWithFilters?.filters?.map((filter) => ({
        value: filter.name,
        label: getTranslation(filter, language),
        icon: "Music",
      })) || [],
    [instrumentWithFilters?.filters, language],
  );

  const genreOptions: TileOption[] = useMemo(
    () =>
      supportedGenres?.map((genre) => ({
        value: genre.name,
        label: getTranslation(genre, language),
        icon: "Music",
      })) || [],
    [language, supportedGenres],
  );

  const languageOptions: TileOption[] = useMemo(
    () =>
      supportedLanguages?.map((lang) => ({
        value: lang.locale,
        label: lang.displayText,
        icon: "Languages",
      })) || [],
    [supportedLanguages],
  );

  const amountOfSelectedFilters = useMemo(() => {
    return (
      searchGenres.length +
      searchInstrumentFilters.length +
      searchLanguages.length
    );
  }, [
    searchGenres.length,
    searchInstrumentFilters.length,
    searchLanguages.length,
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && !(event.target as Element).closest(".filter-panel")) {
        if (
          searchInstrumentFilters.length > 0 ||
          searchGenres.length > 0 ||
          searchLanguages.length > 0
        ) {
          handleSearch();
        }
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [
    handleSearch,
    isOpen,
    searchGenres.length,
    searchInstrumentFilters.length,
    searchLanguages.length,
  ]);

  const resetSearch = () => {
    if (pathname !== "/search") {
      setIsOpen(false);
      return;
    }
    const newParams = new URLSearchParams(searchParams.toString());
    const keysToRemove = ["filters", "genres", "languages"];
    keysToRemove.forEach((key) => newParams.delete(key));
    setSearchInstrumentFilters([]);
    setSearchGenres([]);
    setSearchLanguages([]);

    const queryString = newParams.toString();
    navigate(`${pathname}${queryString ? `?${queryString}` : ""}`);

    setIsOpen(false);
  };

  return (
    <div className={`${className}`}>
      {/* Main Search Button */}
      <button
        className={`border-soluud-border relative flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border bg-white shadow-sm transition-all duration-200 hover:border-gray-300 hover:shadow-[--shadow-card-hover] ${
          isOpen ? "z-50" : ""
        }`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open filters"
        aria-expanded={isOpen}
      >
        <Settings2 size={20} className="text-soluud-text-secondary" />

        {amountOfSelectedFilters > 0 && (
          <span className="bg-soluud-warning absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white text-[10px] font-bold text-white shadow-sm">
            {amountOfSelectedFilters > 99 ? "99+" : amountOfSelectedFilters}
          </span>
        )}
      </button>

      {/* Search Panel */}
      {isOpen && (
        <>
          <SearchModal
            handleSearch={handleSearch}
            searchButtonText={t`Apply Filters`}
            modalIdentifier={"filter-panel"}
          >
            <>
              <div className="flex items-center justify-between">
                <label className="text-soluud-text-primary text-sm font-semibold">
                  <Trans>Filters</Trans>
                </label>
                <button
                  className={
                    "bg-soluud-background flex h-12 w-12 cursor-pointer items-center justify-center rounded-2xl"
                  }
                  onClick={resetSearch}
                >
                  <Trash2 size={20} />
                </button>
              </div>
              <div className={"mb-2"}>
                {instrumentFilterOptions.length > 0 ? (
                  <TileSelector
                    options={instrumentFilterOptions}
                    selectedValues={searchInstrumentFilters}
                    onChange={setSearchInstrumentFilters}
                  />
                ) : (
                  <label className="text-soluud-text-primary text-sm">
                    {instrument ? (
                      <Trans>No filter for this instrument.</Trans>
                    ) : (
                      <Trans>
                        Select an instrument to show possible filters.
                      </Trans>
                    )}
                  </label>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-soluud-text-primary text-sm font-semibold">
                  <Trans>Genres</Trans>
                </label>
              </div>
              <TileSelector
                options={genreOptions}
                selectedValues={searchGenres}
                onChange={setSearchGenres}
              />

              <div className="flex items-center space-x-2">
                <label className="text-soluud-text-primary text-sm font-semibold">
                  <Trans>Teaching languages</Trans>
                </label>
              </div>
              <TileSelector
                options={languageOptions}
                selectedValues={searchLanguages}
                onChange={setSearchLanguages}
              />
            </>
          </SearchModal>
        </>
      )}
    </div>
  );
};

export default Filter;
